/**
 * COP → Dosyalar top-level page.
 *
 * Two "workspaces" (folder tree + file grid + upload dialog + preview modal)
 * share the same UI implementation (`SharedFilesView`), driven by different
 * API adapters:
 *   - "contacts" tab (Kişisel Dosyalar)  → `personalFilesApi` (per-user)
 *   - "shared"   tab (Ortak Dosyalar)   → `sharedFilesApi`   (tenant-wide)
 *
 * The third tab, "construction", still uses its bespoke construction file
 * browser because inşaat files carry a project-scoped metadata model.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Contact2, HardHat, FolderOpen, Paperclip } from "lucide-react";
import { personalFilesApi, sharedFilesApi } from "../../lib/copApi";
import CopFilesConstruction from "./CopFilesConstruction";
import SharedFilesView from "./SharedFilesView";
import SharedFilePreviewModal from "./SharedFilePreviewModal";
import TaskAttachmentsView from "./TaskAttachmentsView";

export default function CopFiles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab =
    tabParam === "shared"
      ? "shared"
      : tabParam === "construction"
        ? "construction"
        : tabParam === "task-attachments"
          ? "task-attachments"
          : "contacts";
  const [tab, setTab] = useState(initialTab);
  const previewFileId = searchParams.get("file");
  const [deepLinkedFile, setDeepLinkedFile] = useState(null);

  // Deep link — `?tab=shared&file=<id>` from email/DM notifications opens
  // the preview modal immediately over the shared workspace.
  useEffect(() => {
    if (tab === "shared" && previewFileId) {
      sharedFilesApi
        .getMeta(previewFileId)
        .then((doc) => setDeepLinkedFile(doc))
        .catch(() => {
          toast.error("Dosya bulunamadı veya erişim yok");
        });
    }
  }, [tab, previewFileId]);

  const switchTab = (next) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === "contacts") params.delete("tab");
    else params.set("tab", next);
    if (next !== "shared") params.delete("file");
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="cop-files-page">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          COP 31
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
          Dosyalar
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          {tab === "contacts"
            ? "Kişisel dosya arşivin — yalnızca senin görebildiğin klasörler ve dosyalar."
            : tab === "shared"
              ? "Tenant'ının ortak arşivi — klasörle organize et, şifreyle koru, ekiple paylaş."
              : tab === "construction"
                ? "Tüm inşaat projelerindeki dosyalar tek yerde — katılımcılara göre filtrele, kategorilere göre gez."
                : "Görevlerine eklenen tüm dosyalar tek listede — filename, görev veya proje ile ara."}
        </p>
      </div>

      <div
        className="inline-flex rounded-lg border border-zinc-200 bg-white p-1 shadow-sm"
        role="tablist"
        data-testid="cop-files-tabbar"
      >
        <TabButton
          active={tab === "contacts"}
          onClick={() => switchTab("contacts")}
          Icon={Contact2}
          label="Kişisel Dosyalar"
          testid="cop-files-tab-contacts"
        />
        <TabButton
          active={tab === "shared"}
          onClick={() => switchTab("shared")}
          Icon={FolderOpen}
          label="Ortak Dosyalar"
          testid="cop-files-tab-shared"
        />
        <TabButton
          active={tab === "construction"}
          onClick={() => switchTab("construction")}
          Icon={HardHat}
          label="İnşaat Dosyaları"
          testid="cop-files-tab-construction"
        />
        <TabButton
          active={tab === "task-attachments"}
          onClick={() => switchTab("task-attachments")}
          Icon={Paperclip}
          label="Görev Ekleri"
          testid="cop-files-tab-task-attachments"
        />
      </div>

      {tab === "construction" ? (
        <CopFilesConstruction />
      ) : tab === "shared" ? (
        // `key` forces a full remount so state (folders, selection,
        // currentFolder) doesn't leak between the two workspaces.
        <SharedFilesView
          key="shared"
          api={sharedFilesApi}
          initialFolderId={searchParams.get("folder")}
        />
      ) : tab === "task-attachments" ? (
        <TaskAttachmentsView />
      ) : (
        <SharedFilesView key="personal" api={personalFilesApi} />
      )}

      {/* Deep-linked preview modal — fires when the URL includes
          ?tab=shared&file=<id> (used by email/DM notifications). */}
      {deepLinkedFile && (
        <SharedFilePreviewModal
          file={deepLinkedFile}
          onClose={() => {
            setDeepLinkedFile(null);
            const params = new URLSearchParams(searchParams);
            params.delete("file");
            setSearchParams(params, { replace: true });
          }}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, Icon, label, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
        active
          ? "bg-zinc-900 text-white shadow-sm"
          : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
